import React from 'react';

import Kit from './Kit.jsx';
import Name from './Name.jsx';
import Token from './Token.jsx';
import TypeConfig from '../common/TypeConfig.jsx';
import Utilities from './Utilities.jsx';

import * as ArtistTypes from '../artist/Types.jsx';
import * as CertificateTypes from '../certificate/Types.jsx';
import * as CommonTypes from '../common/Types.jsx';
import * as ConsignmentTypes from '../consignment/Types.jsx';
import * as InvoiceTypes from '../invoice/Types.jsx';
import * as LayoutTypes from '../layout/Types.jsx';
import * as MosaicTypes from '../mosaic/Types.jsx';
import * as OfferTypes from '../offer/Types.jsx';
import * as PricelistTypes from '../pricelist/Types.jsx';
import * as PurchaseOrderTypes from '../purchaseOrder/Types.jsx';
import * as TearsheetTypes from '../tearsheet/Types.jsx';

const Types = {
    CollateralBlocks: {},

    Document: {
        settings: (type) => {
            const config = {
                id: Utilities.Config.Id.DocumentSettings,
                config: {
                    Style: {
                        group: 'Style',
                        label: 'Document Layout',
                        options: [
                            {
                                disabled: ['marginBottom', 'marginLeft', 'marginRight', 'marginTop'],
                                display: 'Padding',
                                group: 'Style',
                                name: 'paddingMargin',
                                options: TypeConfig.dropdown.measurement(),
                                type: 'paddingMargin',
                                value: {
                                    paddingBottom: { unit: 'px', value: '40' },
                                    paddingLeft: { unit: 'px', value: '40' },
                                    paddingRight: { unit: 'px', value: '40' },
                                    paddingTop: { unit: 'px', value: '40' }
                                }
                            },
                        ]
                    },
                    PDF: {
                        expanded: false,
                        group: 'PDF',
                        // helpText: () => <span>Values are in millimeters (do not include units) and apply to each page<br />NOTE: Builder/preview will not reflect these changes - only PDF exports</span>,
                        label: 'PDF Page Layout',
                        options: [
                            {
                                fullWidth: true,
                                group: 'PDF',
                                type: 'info',
                                value: () => `<span>Values are in millimeters and apply to each page<br />NOTE: Builder/preview will not reflect these changes</span>`
                            },
                            {
                                display: 'Page Margin Top',
                                group: 'PDF',
                                name: 'pageMarginTop',
                                type: 'numeric-positive',
                                value: '10'
                            },
                            {
                                display: 'Page Margin Bottom',
                                group: 'PDF',
                                name: 'pageMarginBottom',
                                type: 'numeric-positive',
                                value: '10'
                            },
                            {
                                display: 'Page Margin Left',
                                group: 'PDF',
                                name: 'pageMarginLeft',
                                type: 'numeric-positive',
                                value: '10'
                            },
                            {
                                display: 'Page Margin Right',
                                group: 'PDF',
                                name: 'pageMarginRight',
                                type: 'numeric-positive',
                                value: '10'
                            },
                            {
                                display: 'Footer Properties',
                                group: 'PDF',
                                id: 'footerProps',
                                type: 'header'
                            },
                            {
                                display: 'Include Footer',
                                fullWidth: true,
                                group: 'PDF',
                                name: 'footer',
                                type: 'checkbox',
                                value: true
                            },
                            {
                                display: 'Use Auto-Generated Address Footer',
                                fullWidth: true,
                                group: 'PDF',
                                name: 'useDefaultFooter',
                                type: 'checkbox',
                                value: true,
                                visible: Types.visibility.hasFooter
                            },
                            {
                                display: 'Text Override',
                                fullWidth: true,
                                group: 'PDF',
                                name: 'textFooter',
                                type: 'text',
                                visible: (config) => Types.visibility.hasFooter(config) && !config.PDF.useDefaultFooter
                            },
                            {
                                allowElementTypeSelection: false,
                                allowTextChanges: false,
                                className: 'config-input-rich-collateral-footer',
                                disabledCommands: ['alignment'],
                                display: 'Footer Design',
                                fullWidth: true,
                                group: 'PDF',
                                name: 'designFooter',
                                type: 'rich-text-collateral-short',
                                visible: Types.visibility.hasFooter
                            },
                            {
                                disabled: ['marginBottom', 'marginLeft', 'marginRight', 'marginTop'],
                                display: 'Padding',
                                group: 'PDF',
                                name: 'paddingMarginFooter',
                                nameSuffix: 'Footer',
                                options: TypeConfig.dropdown.measurement(),
                                type: 'paddingMargin',
                                visible: Types.visibility.hasFooter
                            }
                        ]
                    }
                }
            }
            switch (type) {
                case Name.Config.ArtistBio:
                case Name.Config.CertificateOfAuthenticity:
                case Name.Config.Mosaic:
                case Name.Config.Tearsheet:
                    config.config.Style.options.splice(0, 0, {
                        display: 'Edit Page Header',
                        fullWidth: true,
                        group: 'Style',
                        offsetTop: true,
                        type: 'edit-button',
                        value: {
                            keyPath: ['Style', 'pageHeader'],
                            name: Name.Content.PageHeader
                        }
                    });
                    break;
            }
            return config;
        },
        migrateLegacyFooterDesign: (pdfConfig) => {
            if (!pdfConfig || pdfConfig.designFooter) {
                return null;
            }
            const styles = [`font-family: 'Arial', 'Verdana'`];
            if (pdfConfig.colorFooter) {
                styles.push(`color: ${pdfConfig.colorFooter}`);
            } else {
                styles.push('color: grey');
            }
            if (pdfConfig.fontStyleFooter) {
                styles.push(`font-style: ${pdfConfig.fontStyleFooter}`);
            }
            if (pdfConfig.fontWeightFooter) {
                styles.push(`font-weight: ${pdfConfig.fontWeightFooter}`);
            }
            let fontSize = '8pt';
            if (pdfConfig.fontSizeFooter && pdfConfig.fontSizeFooter.value && pdfConfig.fontSizeFooter.value.length > 0) {
                fontSize = `${pdfConfig.fontSizeFooter.value}${pdfConfig.fontSizeFooter.unit}`;
            }
            styles.push(`font-size: ${fontSize}`);

            const isDefault = !pdfConfig.textFooter;
            const design = Utilities.wrapTextForEditor({
                text: `{${Token.FooterText}}`,
                textExtras: styles.length ? `style="${styles.join('; ')};"` : undefined,
                token: Token.FooterText,
                wrapWithTag: false
            });
            return { design, fontSize, isDefault };
        },
        getPDFParameters: (config, collateralData) => {
            let params;
            const configProps = config && config.Config && config.Config.document && config.Config.document.config && config.Config.document.config.PDF;
            if (configProps) {
                const footer = configProps.footer != undefined ? configProps.footer : true;
                params = {
                    Footer: {},
                    Margins: {
                        Bottom: configProps.pageMarginBottom,
                        Left: configProps.pageMarginLeft,
                        Right: configProps.pageMarginRight,
                        Top: configProps.pageMarginTop
                    },
                    Includes: { Footer: footer }
                };
                if (footer) {
                    const migrated = Types.Document.migrateLegacyFooterDesign(configProps);
                    let design = configProps.designFooter || migrated?.design;
                    let footerText = configProps.textFooter;
                    if (configProps.useDefaultFooter || !footerText || footerText.length == 0) {
                        footerText = collateralData && collateralData.FooterText;
                    }
                    if (design) {
                        design = design.replaceAll(`{${Token.FooterText}}`, footerText || '')
                    }
                    if (migrated?.fontSize) {
                        params.Footer.FontSize = migrated.fontSize;
                    } else if (design) {
                        const styles = Utilities.parseStyleString(Utilities.extractStyle(design));
                        if (styles['line-height']) {
                            params.Footer.FontSize = styles['line-height'];
                        } else if (styles['font-size']) {
                            params.Footer.FontSize = styles['font-size'];
                        }
                    }
                    if (collateralData?.Styleguide?.Fonts?.LinkHref) {
                        design = `<link rel="stylesheet" href="${collateralData.Styleguide.Fonts.LinkHref}" />${design}`;
                    }
                    design = design.replaceAll('<p', '<span').replaceAll('/p>', '/span>');
                    params.Footer.Design = design;
                    if (configProps.paddingBottomFooter && configProps.paddingBottomFooter.value && configProps.paddingBottomFooter.value.length > 0) {
                        params.Footer.PaddingBottom = `${configProps.paddingBottomFooter.value}${configProps.paddingBottomFooter.unit}`;
                    }
                    if (configProps.paddingLeftFooter && configProps.paddingLeftFooter.value && configProps.paddingLeftFooter.value.length > 0) {
                        params.Footer.PaddingLeft = `${configProps.paddingLeftFooter.value}${configProps.paddingLeftFooter.unit}`;
                    }
                    if (configProps.paddingRightFooter && configProps.paddingRightFooter.value && configProps.paddingRightFooter.value.length > 0) {
                        params.Footer.PaddingRight = `${configProps.paddingRightFooter.value}${configProps.paddingRightFooter.unit}`;
                    }
                    if (configProps.paddingTopFooter && configProps.paddingTopFooter.value && configProps.paddingTopFooter.value.length > 0) {
                        params.Footer.PaddingTop = `${configProps.paddingTopFooter.value}${configProps.paddingTopFooter.unit}`;
                    }
                }
            }
            return params;
        }
    },

    defaultConfig: (type) => {
        const documentConfig = {
            blocks: [],
            document: {
                config: {},
                id: Utilities.Config.Id.DocumentSettings
            }
        };
        const config = Types.Document.settings(type).config;
        for (let id in config) {
            documentConfig.document.config[id] = Utilities.Config.getMap(config[id], 'options');
        }
        return documentConfig;
    },
    getBlock: (id, type, data) => {
        let contentBlocks = Types.CollateralBlocks[type];
        if (!contentBlocks) {
            return null;
        }
        contentBlocks = contentBlocks(data);
        if (!contentBlocks) {
            return null;
        }
        let temp;
        for (let i = 0; i < contentBlocks.length; i++) {
            temp = contentBlocks[i];
            if (temp.id != id) {
                continue;
            }
            return temp;
        }
        return null;
    },
    getBlockTokens: (block, type, data) => {
        if (!block) {
            return null;
        }
        switch (block.id) {
            case Name.Block.Company.ContactMeta:
                return CommonTypes.Tokens.get(block.id);
        }
        switch (type) {
            case Name.Config.ArtistBio:
                return ArtistTypes.Tokens.get(block.id, data);
            case Name.Config.CertificateOfAuthenticity:
                return CertificateTypes.Tokens.get(block.id, data);
            case Name.Config.Consignment:
                return ConsignmentTypes.Tokens.get(block.id, data);
            case Name.Config.Invoice:
                return InvoiceTypes.Tokens.get(block.id, data);
            case Name.Config.Mosaic:
                return MosaicTypes.Tokens.get(block.id, data);
            case Name.Config.Offer:
                return OfferTypes.Tokens.get(block.id, data);
            case Name.Config.Pricelist:
                return PricelistTypes.Tokens.get(block.id, data);
            case Name.Config.PurchaseOrder:
                return PurchaseOrderTypes.Tokens.get(block.id, data);
            case Name.Config.Tearsheet:
                return TearsheetTypes.Tokens.get(block.id, data);
        }
    },
    getDisplay: (blockType, configType) => {
        if (!blockType) {
            return 'Unknown Block';
        }
        switch (blockType.value) {
            case Name.Block.Artist.Container:
                return 'Artist Container';
            case Name.Block.Artist.Image:
                return 'Artist Image';
            case Name.Block.Artist.Meta:
                return 'Artist Meta';
            case Name.Block.AuthenticityStatement:
                return 'Authenticity Statement'
            case Name.Block.Client.ContactMeta:
                return 'Customer Contact Info';
            case Name.Block.Columns:
                return 'Columns';
            case Name.Block.Company.ContactMeta:
                return 'Company Contact Info';
            case Name.Block.Company.Logo:
                return 'Logo';
            case Name.Block.Company.StaffName:
                return 'User Name';
            case Name.Block.Consignment.Meta:
                return 'Consignment Meta';
            case Name.Block.Consignment.Terms:
                return 'Consignment Terms';
            case Name.Block.Consignment.Totals:
                return 'Totals';
            case Name.Block.Date:
                switch (configType) {
                    case Name.Config.Consignment:
                        return 'Term Date';
                    case Name.Config.Invoice:
                        return 'Invoice Date';
                    case Name.Config.Offer:
                        return 'Offer Date';
                    case Name.Config.PurchaseOrder:
                        return 'Purchase Order Date';
                }
                return 'Date';
            case Name.Block.Inventory.Container:
                return 'Item Container';
            case Name.Block.Inventory.Image:
                return 'Item Image';
            case Name.Block.Inventory.List:
                switch (configType) {
                    case Name.Config.Invoice:
                    case Name.Config.Offer:
                    case Name.Config.PurchaseOrder:
                        return 'Line Items';
                }
                return 'Items List';
            case Name.Block.Inventory.Meta:
            case Name.Block.ListOrderNumber:
                return 'Item Meta';
            case Name.Block.ListTitle:
                return 'List Title';
            case Name.Block.Name:
                switch (configType) {
                    case Name.Config.PurchaseOrder:
                        return 'Purchase Order Name';
                }
                return 'Name';
            case Name.Block.NonBreakingContainer:
                return 'Non-Breaking Container';
            case Name.Block.Notes:
                return 'Notes';
            case Name.Block.Number:
                switch (configType) {
                    case Name.Config.Invoice:
                        return 'Invoice Number';
                    case Name.Config.PurchaseOrder:
                        return 'Purchase Order Number';
                }
                return 'Number';
            case Name.Block.PaymentCredit.Amount:
            case Name.Block.PaymentCredit.Date:
            case Name.Block.PaymentCredit.Method:
                return 'Payment Meta';
            case Name.Block.PaymentCredit.CreditsContainer:
                return 'Credits Table';
            case Name.Block.PaymentCredit.PaymentsContainer:
                return 'Payments Table';
            case Name.Block.Separator:
                return 'Divider';
            case Name.Block.Signature:
                return 'Signature Line';
            case Name.Block.Stage:
                return 'Stage';
            case Name.Block.TaxLine:
                return 'GST Tax Line';
            case Name.Block.Text:
                return 'Text';
            case Name.Block.Total.Balance:
                return 'Balance';
            case Name.Block.Total.Discount:
                return 'Discount';
            case Name.Block.Total.GrandTotal:
                return 'Grand Total';
            case Name.Block.Total.Paid:
                return 'Total Paid';
            case Name.Block.Total.PromoCodes:
                return 'Promo Codes';
            case Name.Block.Total.Refunded:
                return 'Total Refunded';
            case Name.Block.Total.Shipping:
                return 'Shipping';
            case Name.Block.Total.Subtotal:
                return 'Subtotal';
            case Name.Block.Total.Taxes:
                return 'Taxes';
            case Name.Block.TrackingNumber:
                return 'Tracking Number';
            case Name.Block.ValidThrough:
                return 'Valid Through';
            default:
                return `Unknown block type value: ${blockType.value}`;
        }
    },
    getTitle: (type) => {
        switch (type) {
            case Name.Config.ArtistBio:
                return 'Artist Bio';
            case Name.Config.CertificateOfAuthenticity:
                return 'Certificate of Authenticity';
            case Name.Config.Consignment:
                return 'Consignment';
            case Name.Config.Invoice:
                return 'Invoice';
            case Name.Config.Mosaic:
                return 'Mosaic';
            case Name.Config.Offer:
                return 'Offer';
            case Name.Config.Pricelist:
                return 'Pricelist';
            case Name.Config.PurchaseOrder:
                return 'Purchase Order';
            case Name.Config.Tearsheet:
                return 'Tearsheet';
            default:
                return '';
        }
    },
    hasPageHeader: (type) => {
        switch (type) {
            case Name.Config.ArtistBio:
            case Name.Config.CertificateOfAuthenticity:
            case Name.Config.Mosaic:
            case Name.Config.Tearsheet:
                return true;
            default:
                return false;
        }
    },
    isBlockAllowedInsideLayout: (blockType, parentLayoutType) => {
        switch (parentLayoutType) {
            case Name.Block.Columns:
                switch (blockType) {
                    case Name.Block.Columns:
                    case Name.Block.Inventory.List:
                    case Name.Block.NonBreakingContainer:
                        return false;
                }
                break;
            case Name.Block.NonBreakingContainer:
                switch (blockType) {
                    case Name.Block.NonBreakingContainer:
                        return false;
                }
        }
        return true;
    },

    visibility: {
        hasFooter: (config) => config.PDF && config.PDF.footer
    },

    Kit,
    Name,
    Token
};

// #region CONFIG TYPES

Types.CollateralBlocks[Name.Config.ArtistBio] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...ArtistTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Config.CertificateOfAuthenticity] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...CertificateTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Config.Consignment] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...ConsignmentTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Config.Invoice] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...InvoiceTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Config.Mosaic] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...MosaicTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Config.Offer] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...OfferTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Config.Pricelist] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...PricelistTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Config.PurchaseOrder] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...PurchaseOrderTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Config.Tearsheet] = (data) => [
    ...CommonTypes.Blocks.get(0),
    ...TearsheetTypes.Blocks.get(0, data),
    ...LayoutTypes.Blocks.get(1)
];

// #endregion CONFIG TYPES

// #region CONTENT TYPES

Types.CollateralBlocks[Name.Content.ArtistBioItem] = () => [
    ...ArtistTypes.Content[Name.Block.Artist.Container].get(0),
    ...LayoutTypes.Blocks.get(1, Name.Content.ArtistBioItem)
];

Types.CollateralBlocks[Name.Content.ArtistInventoryItem] = () => [
    ...ArtistTypes.Content[Name.Block.Inventory.Container].get(0),
    ...LayoutTypes.Blocks.get(1, Name.Content.ArtistInventoryItem)
];

Types.CollateralBlocks[Name.Content.CertificateItem] = (data) => [
    ...CertificateTypes.Content[Name.Block.Inventory.Container].get(0, data),
    ...LayoutTypes.Blocks.get(1, Name.Content.CertificateItem)
];

Types.CollateralBlocks[Name.Content.ConsignmentLineItem] = (data) => [
    ...ConsignmentTypes.Content[Name.Block.Inventory.List].get(0, data),
    ...LayoutTypes.Blocks.get(1, Name.Content.ConsignmentLineItem)
];

Types.CollateralBlocks[Name.Content.CreditLineItem] = (data) => [
    ...InvoiceTypes.Content[Name.Block.PaymentCredit.CreditsContainer].get(0, data),
    ...LayoutTypes.Blocks.get(1, Name.Content.CreditLineItem)
];

Types.CollateralBlocks[Name.Content.InvoiceLineItem] = () => [
    ...InvoiceTypes.Content[Name.Block.Inventory.List].get(0),
    ...LayoutTypes.Blocks.get(1, Name.Content.InvoiceLineItem)
];

Types.CollateralBlocks[Name.Content.MosaicItem] = (data) => [
    ...MosaicTypes.Content[Name.Block.Inventory.Container].get(0, data),
    ...LayoutTypes.Blocks.get(1, Name.Content.MosaicItem)
];

Types.CollateralBlocks[Name.Content.PageHeader] = () => [
    ...CommonTypes.Blocks.get(0),
    ...LayoutTypes.Blocks.get(1)
];

Types.CollateralBlocks[Name.Content.PaymentLineItem] = (data) => [
    ...InvoiceTypes.Content[Name.Block.PaymentCredit.PaymentsContainer].get(0, data),
    ...LayoutTypes.Blocks.get(1, Name.Content.PaymentLineItem)
];

Types.CollateralBlocks[Name.Content.PricelistInventoryItem] = () => [
    ...PricelistTypes.Content[Name.Block.Inventory.List].get(0),
    ...LayoutTypes.Blocks.get(1, Name.Content.PricelistInventoryItem)
];

Types.CollateralBlocks[Name.Content.PurchaseOrderLineItem] = () => [
    ...PurchaseOrderTypes.Content[Name.Block.Inventory.List].get(0),
    ...LayoutTypes.Blocks.get(1, Name.Content.PurchaseOrderLineItem)
];

Types.CollateralBlocks[Name.Content.TearsheetItem] = (data) => [
    ...TearsheetTypes.Content[Name.Block.Inventory.Container].get(0, data),
    ...LayoutTypes.Blocks.get(1, Name.Content.TearsheetItem)
];

// #endregion CONTENT TYPES

export default Types;
